import styled from 'styled-components'
import { FC } from 'react'
import MenuButton from '../../../../shared/MenuButton'
import Hint from '../../../../shared/hint/Hint'
import FilterModal from './FilterModal'
import DataAccessFilterModal from './DataAccessFilterModal'
import CostFilterModal from './CostFilterModal'
import { useModal } from '../../../../../hooks/useModalState'
import { ReactComponent as ArrowIcon } from '../../assets/Arrow.svg'
import { ReactComponent as CrossIcon } from '../../assets/Cross.svg'
import { TFilter, TFilterShortName } from '../../definitions'
import { FILTER_HINTS_MAP } from '../../constants'
import { FilterProvider, useFilterContext } from '../../state/useFilter'
import { useFiltersStateContext } from '../../state/useFiltersState'

const IconsContainerStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CounterStyled = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.blue.primary};
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const FilterButton: FC = () => {
  const [open, closeModal, openModal] = useModal()
  const { filterData: { shortName, name, subFilters } } = useFilterContext()
  const { getFilterValues, clearFilterValues } = useFiltersStateContext()
  const isCostFilter = shortName === 'cost'
  // "Cost" also has subFilters (for building its modal), but unlike "Data access" it's
  // submitted/stored as one combined value under its own shortName, not per sub-shortName.
  const isGrouped = !!subFilters?.length && !isCostFilter
  const groupedShortNames = (subFilters?.map((filter) => filter.shortName) ?? []) as TFilterShortName[]

  const selectedValues = getFilterValues(shortName) as any
  const selectedCount = isGrouped
    ? groupedShortNames.reduce(
      (acc, subShortName) => acc + ((getFilterValues(subShortName) as any)?.filters?.length ?? 0),
      0
    )
    : selectedValues?.filters?.length ?? 0
  const isActive = selectedCount > 0

  const clearValues = () => {
    if (isGrouped) {
      groupedShortNames.forEach((subShortName) => clearFilterValues(subShortName))
    } else {
      clearFilterValues(shortName)
    }
  }

  return (
    <>
      <MenuButton
        onClick={openModal}
        startIcon={<Hint placement="top" iconWidth={20} title={FILTER_HINTS_MAP[shortName]} />}
        fullWidth
        isActive={isActive}
        endIcon={(
          <IconsContainerStyled>
            {isActive && (
              <>
                <CounterStyled>{selectedCount}</CounterStyled>
                <CrossIcon
                  onClick={(e: any) => {
                    e.stopPropagation()
                    clearValues()
                  }}
                />
              </>
            )}
            <ArrowIcon />
          </IconsContainerStyled>
        )}
      >
        {name}
      </MenuButton>
      {/* eslint-disable-next-line no-nested-ternary */}
      {isCostFilter
        ? <CostFilterModal open={open} closeModal={closeModal} />
        : isGrouped
          ? <DataAccessFilterModal open={open} closeModal={closeModal} />
          : <FilterModal open={open} closeModal={closeModal} />}
    </>
  )
}

export default (props: { filterData: TFilter }) => (
  <FilterProvider {...props}>
    <FilterButton />
  </FilterProvider>
)
